import { memo } from 'react';
import { ShoppingCart, List, ArrowLeftRight, Edit2, CheckSquare, Tag, Store } from 'lucide-react';
import { EconomyLogMessage } from '../types/player';
import { PlayerProfile } from './PlayerProfile';
import { ItemIcon } from './ItemIcon';

export const TransactionLogEntry = memo(({ entry, clusterId }: { entry: EconomyLogMessage, clusterId: string }) => {
    const { action, money, user, itemDisplayName, from, to, count, item } = entry;
  
    const renderIcon = () => {
      switch(action) {
        case 'BUY_AH': return <ShoppingCart className="w-4 h-4 text-green-400" />;
        case 'SELL_AH': return <ShoppingCart className="w-4 h-4 text-red-400" />;
        case 'LISTED_AH': return <Tag className="w-4 h-4 text-blue-400" />;
        case 'BUY_AS': return <Store className="w-4 h-4 text-green-400" />;
        case 'SELL_AS': return <Store className="w-4 h-4 text-red-400" />;
        case 'BUY_CS': return <ShoppingCart className="w-4 h-4 text-green-400" />;
        case 'SELL_CS': return <ShoppingCart className="w-4 h-4 text-red-400" />;
        case 'SENT': return <ArrowLeftRight className="w-4 h-4 text-orange-400" />;
        case 'RECEIVED': return <ArrowLeftRight className="w-4 h-4 text-green-400" />;
        case 'MOD': return <Edit2 className="w-4 h-4 text-yellow-400" />;
        case 'SET': return <CheckSquare className="w-4 h-4 text-yellow-400" />;
        default: return <List className="w-4 h-4 text-glass-400" />;
      }
    };
    
    const renderMessage = () => {
      const countNum = count ? parseInt(count.toString()) : 1;
      const itemCount = countNum > 1 ? `${countNum}x ` : '';
      
      const itemComponent = item 
        ? <ItemIcon displayMode="inline" clusterId={clusterId} itemId={item} initialDisplayName={itemDisplayName} /> 
        : (itemDisplayName || '');
      
      switch(action) {
        case 'BUY_AH':
          return <><PlayerProfile key={user} uuid={user} /> bought {itemCount} {itemComponent} for ₡{money}</>;
        case 'SELL_AH':
          return <><PlayerProfile key={user} uuid={user} /> sold {itemCount} {itemComponent} for ₡{money}</>;
        case 'LISTED_AH':
          return <><PlayerProfile key={user} uuid={user} /> listed {itemCount} {itemComponent} for ₡{money}</>;
        case 'BUY_AS':
          return <><PlayerProfile key={user} uuid={user} /> bought {itemCount} {itemComponent} from admin shop for ₡{money}</>;
        case 'SELL_AS': 
          return <><PlayerProfile key={user} uuid={user} /> sold {itemCount} {itemComponent} to admin shop for ₡{money}</>;
        case 'BUY_CS':
          return <><PlayerProfile key={user} uuid={user} /> bought {itemCount} {itemComponent} from chest shop for ₡{money}</>;
        case 'SELL_CS':
          return <><PlayerProfile key={user} uuid={user} /> sold {itemCount} {itemComponent} to chest shop for ₡{money}</>;
        case 'SENT':
          return <><PlayerProfile key={user} uuid={user} /> sent ₡{money} to <PlayerProfile key={to!} uuid={to!} /></>;
        case 'RECEIVED':
          return <><PlayerProfile key={user} uuid={user} /> received ₡{money} from <PlayerProfile key={from!} uuid={from!} /></>;
        case 'MOD':
          const amount = parseInt(money);
          const sign = amount > 0 ? '+' : '';
          const color = amount > 0 ? 'text-green-400' : 'text-red-400';
          return <>Balance for <PlayerProfile key={user} uuid={user} /> modified by <span className={color}>{sign}₡{money}</span></>;
        case 'SET':
          return <>Balance for <PlayerProfile key={user} uuid={user} /> set to ₡{money}</>;
        default:
          return <span className="font-mono text-xs">{JSON.stringify(entry)}</span>;
      }
    };
  
    return (
      <div className="flex items-start gap-3">
        <span className="shrink-0 mt-0.5">{renderIcon()}</span>
        <div className="text-sm min-w-0 leading-5 tabular-nums">{renderMessage()}</div>
      </div>
    );
});
